class ProductCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.track = this.querySelector('[data-carousel-track]');
    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
    this.dotList = this.querySelector('.product-carousel__dots');
    this.playback = this.querySelector('[data-carousel-playback]');
    this.status = this.querySelector('[data-carousel-status]');
    if (!this.track || this.slides.length === 0 || this.dots.length !== this.slides.length) return;

    this.initialized = true;
    this.activeIndex = 0;
    this.appliedIndex = -1;
    this.autoplayDelay = Number(this.dataset.autoplayDelay) || 6000;
    /* Touch-primary screens stay manual: autoplay under a thumb fights the
       swipe and tested badly with real users (2026-08-17). Desktop keeps it. */
    const touchPrimary = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1 && !touchPrimary;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.userPaused = false;
    this.inView = false;
    this.hovered = false;
    this.focusWithin = false;
    this.dragging = false;
    this.suppressClick = false;
    this.pointer = null;
    this.animationFrame = 0;
    this.scrollFrame = 0;
    this.dragFrame = 0;
    this.scrollTimer = 0;
    this.autoplayTimer = 0;
    this.suppressClickTimer = 0;

    this.onScroll = this.onScroll.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerEnd = this.onPointerEnd.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);

    /* Every listener shares one signal, so a disconnect followed by a reconnect
       (a Theme Editor section reorder moves the element) never binds twice. */
    this.listeners = new AbortController();
    const signal = this.listeners.signal;

    this.track.addEventListener('scroll', this.onScroll, { passive: true, signal });
    this.track.addEventListener('pointerdown', this.onPointerDown, { signal });
    this.track.addEventListener('pointermove', this.onPointerMove, { signal });
    /* The release is heard on window: a mouse press that never commits to a
       horizontal drag has no pointer capture, and letting go outside the track
       would otherwise leave the carousel stuck in its dragging state. */
    window.addEventListener('pointerup', this.onPointerEnd, { signal });
    window.addEventListener('pointercancel', this.onPointerEnd, { signal });
    /* Non-passive on purpose: once a gesture commits to the horizontal axis we
       must block the browser's pan-y from stealing the rest of the gesture. */
    this.track.addEventListener('touchmove', this.onTouchMove, { passive: false, signal });
    this.track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    this.track.addEventListener(
      'wheel',
      (event) => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey) this.pauseByUser();
      },
      { passive: true, signal }
    );
    this.track.addEventListener(
      'click',
      (event) => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
      },
      { capture: true, signal }
    );

    this.dots.forEach((dot, index) => {
      dot.addEventListener(
        'click',
        () => {
          this.pauseByUser();
          this.animateTo(index, 0, true);
        },
        { signal }
      );
      dot.addEventListener('keydown', (event) => this.onDotKeydown(event, index), { signal });
    });

    if (this.playback) this.playback.addEventListener('click', () => this.togglePlayback(), { signal });

    this.addEventListener(
      'pointerenter',
      (event) => {
        if (event.pointerType === 'touch') return;
        this.hovered = true;
        this.syncAutoplay();
      },
      { signal }
    );
    this.addEventListener(
      'pointerleave',
      (event) => {
        if (event.pointerType === 'touch') return;
        this.hovered = false;
        this.syncAutoplay();
      },
      { signal }
    );
    this.addEventListener(
      'focusin',
      () => {
        this.focusWithin = true;
        this.syncAutoplay();
      },
      { signal }
    );
    this.addEventListener(
      'focusout',
      () => {
        requestAnimationFrame(() => {
          this.focusWithin = this.contains(document.activeElement);
          this.syncAutoplay();
        });
      },
      { signal }
    );

    document.addEventListener('visibilitychange', () => this.syncAutoplay(), { signal });
    this.reducedMotion.addEventListener('change', () => this.onMotionChange(), { signal });

    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.35;
          this.syncAutoplay();
        },
        { threshold: [0, 0.35, 0.7] }
      );
      this.visibilityObserver.observe(this);
    } else {
      this.inView = true;
    }

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.dragging || this.animationFrame) return;
        this.track.scrollLeft = this.targetScroll(this.activeIndex);
      });
      this.resizeObserver.observe(this.track);
    }

    this.setActive(0, false);
    this.syncPlaybackAvailability();
    this.syncAutoplay();
  }

  disconnectedCallback() {
    if (!this.initialized) return;
    this.stopAnimation();
    this.clearAutoplay();
    clearTimeout(this.scrollTimer);
    clearTimeout(this.suppressClickTimer);
    cancelAnimationFrame(this.scrollFrame);
    cancelAnimationFrame(this.dragFrame);
    this.dragFrame = 0;
    this.visibilityObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.listeners?.abort();
    this.pointer = null;
    this.dragging = false;
    this.classList.remove('is-dragging');
    this.initialized = false;
  }

  onMotionChange() {
    this.stopAnimation();
    this.syncPlaybackAvailability();
    this.syncAutoplay();
  }

  onDotKeydown(event, index) {
    let target = index;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = (index + 1) % this.dots.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = (index - 1 + this.dots.length) % this.dots.length;
    else if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = this.dots.length - 1;
    else return;

    event.preventDefault();
    this.pauseByUser();
    this.dots[target].focus();
    this.animateTo(target, 0, true);
  }

  onPointerDown(event) {
    if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;

    this.pointer = {
      id: event.pointerId,
      type: event.pointerType,
      startX: event.clientX,
      startY: event.clientY,
      startScroll: this.track.scrollLeft,
      moved: false,
      committed: false,
      samples: [{ x: event.clientX, time: performance.now() }]
    };
    this.dragging = true;
    this.syncAutoplay();

    /* Contact kills any active spring for every pointer type. */
    this.stopAnimation();
    /* And CSS snap stays off while the finger is down: stopAnimation restores
       mandatory snap, which would yank a mid-card track the instant it is
       touched — felt as a snatch/tremble on grab. Release paths restore it. */
    this.track.style.scrollSnapType = 'none';

    /* Keep ordinary clicks targeted at the product link. Pointer capture is
       deferred until horizontal drag intent is confirmed in onPointerMove. */
  }

  /* The track owns the horizontal axis (touch-action: pan-y leaves vertical to
     the browser). Blocking pan-y is only allowed AFTER a gesture has committed
     horizontally — that is what keeps vertical flicks native and silky. */
  onTouchMove(event) {
    if (this.pointer && this.pointer.committed) event.preventDefault();
  }

  onPointerMove(event) {
    if (!this.pointer || this.pointer.id !== event.pointerId) return;

    const dx = event.clientX - this.pointer.startX;
    const dy = event.clientY - this.pointer.startY;

    if (!this.pointer.committed) {
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;

      /* Axis intent decided once per gesture, at ~10px. Vertical-dominant touch
         gestures are handed to the browser untouched: we simply stop tracking
         and native page scroll proceeds with full momentum. */
      if (this.pointer.type === 'touch' && Math.abs(dy) >= Math.abs(dx)) {
        this.pointer = null;
        this.dragging = false;
        this.track.style.removeProperty('scroll-snap-type');
        this.syncAutoplay();
        return;
      }
      if (Math.abs(dx) < 10) return;

      this.pointer.committed = true;
      this.pointer.moved = true;
      if (!this.track.hasPointerCapture(event.pointerId)) {
        this.track.setPointerCapture(event.pointerId);
      }
      this.pauseByUser();
      this.classList.add('is-dragging');
      this.track.style.scrollSnapType = 'none';
    }

    const now = performance.now();
    this.pointer.samples.push({ x: event.clientX, time: now });
    this.pointer.samples = this.pointer.samples.filter((sample) => now - sample.time <= 120);

    if (this.pointer.type !== 'touch') event.preventDefault();

    /* 1:1 from the grab point, with progressive resistance past the ends —
       a hard clamp reads as frozen, rubber-band reads as "nothing more here". */
    let next = this.pointer.startScroll - dx;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const width = this.track.clientWidth;
    if (next < 0) {
      const over = -next;
      next = -((over * width * 0.55) / (width + 0.55 * over));
    } else if (next > max) {
      const over = next - max;
      next = max + (over * width * 0.55) / (width + 0.55 * over);
    }

    /* Input events outrun the display; writing scrollLeft per event fights the
       frame cadence and judders. Buffer the latest target, one write per frame. */
    this.pointer.targetScroll = next;
    if (!this.dragFrame) {
      this.dragFrame = requestAnimationFrame(() => {
        this.dragFrame = 0;
        if (this.pointer && this.pointer.committed) {
          this.track.scrollLeft = this.pointer.targetScroll;
        }
      });
    }
  }

  onPointerEnd(event) {
    if (!this.pointer || this.pointer.id !== event.pointerId) return;

    const pointer = this.pointer;
    this.pointer = null;
    this.dragging = false;
    this.classList.remove('is-dragging');

    if (this.dragFrame) {
      cancelAnimationFrame(this.dragFrame);
      this.dragFrame = 0;
    }
    if (pointer.committed && pointer.targetScroll != null) {
      this.track.scrollLeft = pointer.targetScroll;
    }

    if (!pointer.moved) {
      this.track.style.removeProperty('scroll-snap-type');
      this.syncAutoplay();
      return;
    }

    this.suppressClick = true;
    clearTimeout(this.suppressClickTimer);
    this.suppressClickTimer = window.setTimeout(() => {
      this.suppressClick = false;
    }, 450);

    /* Release keeps the finger's velocity and decays it with real friction —
       stepless, travelling through every card on the way — then settles onto
       the nearest card only once the momentum has nearly died. */
    const samples = pointer.samples;
    const last = samples[samples.length - 1];
    const first = samples[0] || last;
    const elapsed = Math.max(last.time - first.time, 16);
    const velocity = -((last.x - first.x) / elapsed) * 1000;
    this.animateFling(velocity);
    this.syncAutoplay();
  }

  /* Native-style inertia: exponential decay (0.998 per ms), clamped at the ends,
     handing its residual velocity to the settle spring. Interruptible at any
     frame — pointerdown's stopAnimation cancels it. */
  animateFling(initialVelocity) {
    this.stopAnimation();

    if (this.reducedMotion.matches || Math.abs(initialVelocity) < 240) {
      this.animateTo(this.nearestIndex(this.track.scrollLeft), initialVelocity, true);
      return;
    }

    this.track.style.scrollSnapType = 'none';
    this.classList.add('is-animating');

    let position = this.track.scrollLeft;
    let velocity = initialVelocity;
    let previous = performance.now();
    const max = this.track.scrollWidth - this.track.clientWidth;

    const step = (now) => {
      const dt = Math.min(now - previous, 1000 / 30);
      previous = now;
      position += velocity * (dt / 1000);
      velocity *= Math.pow(0.998, dt);
      if (position <= 0 || position >= max) {
        position = Math.max(0, Math.min(position, max));
        velocity = 0;
      }
      this.track.scrollLeft = position;

      if (Math.abs(velocity) < 240) {
        this.animationFrame = 0;
        this.animateTo(this.nearestIndex(position), velocity, true);
        return;
      }
      this.animationFrame = requestAnimationFrame(step);
    };

    this.animationFrame = requestAnimationFrame(step);
  }

  onScroll() {
    if (this.animationFrame) return;
    /* While a finger owns the track, is-current must not flip: hovering near a
       midpoint would restart the slide scale/opacity transitions every few
       pixels and read as trembling. Everything settles once at release. */
    if (this.dragging) return;
    if (!this.scrollFrame) {
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        this.setActive(this.nearestIndex(this.track.scrollLeft), false);
      });
    }

    clearTimeout(this.scrollTimer);
    this.scrollTimer = window.setTimeout(() => {
      if (this.animationFrame || this.dragging) return;
      this.setActive(this.nearestIndex(this.track.scrollLeft), false);
    }, 120);
  }

  targetScroll(index) {
    const slide = this.slides[index];
    const centered = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
    return Math.max(0, Math.min(centered, this.track.scrollWidth - this.track.clientWidth));
  }

  nearestIndex(position) {
    let closest = 0;
    let distance = Infinity;
    this.slides.forEach((slide, index) => {
      const delta = Math.abs(this.targetScroll(index) - position);
      if (delta < distance) {
        distance = delta;
        closest = index;
      }
    });
    return closest;
  }

  animateTo(index, initialVelocity = 0, announce = false) {
    const target = this.targetScroll(index);
    this.stopAnimation();
    this.setActive(index, announce);

    if (this.reducedMotion.matches || Math.abs(this.track.scrollLeft - target) < 1) {
      this.track.scrollLeft = target;
      this.track.style.removeProperty('scroll-snap-type');
      return;
    }

    this.track.style.scrollSnapType = 'none';
    this.classList.add('is-animating');

    let position = this.track.scrollLeft;
    let velocity = initialVelocity;
    let previous = performance.now();
    const omega = (2 * Math.PI) / 0.4;

    const step = (now) => {
      const elapsed = Math.min((now - previous) / 1000, 1 / 30);
      previous = now;
      const acceleration = -2 * omega * velocity - omega * omega * (position - target);
      velocity += acceleration * elapsed;
      position += velocity * elapsed;
      this.track.scrollLeft = position;

      if (Math.abs(target - position) < 0.5 && Math.abs(velocity) < 5) {
        this.track.scrollLeft = target;
        this.animationFrame = 0;
        this.track.style.removeProperty('scroll-snap-type');
        this.classList.remove('is-animating');
        return;
      }
      this.animationFrame = requestAnimationFrame(step);
    };

    this.animationFrame = requestAnimationFrame(step);
  }

  stopAnimation() {
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    this.animationFrame = 0;
    this.classList.remove('is-animating');
    this.track?.style.removeProperty('scroll-snap-type');
  }

  setActive(index, announce) {
    if (index < 0 || index >= this.slides.length) return;

    /* onScroll calls this every frame; rewriting classes, aria, inert, and
       smooth-scrolling the dot rail per frame made touch tracking stutter.
       Nothing below changes unless the index does. */
    if (this.appliedIndex === index) {
      if (announce && this.status) this.status.textContent = this.statusText(index);
      return;
    }
    this.appliedIndex = index;
    this.activeIndex = index;

    this.slides.forEach((slide, slideIndex) => {
      const current = slideIndex === index;
      slide.classList.toggle('is-current', current);
      slide.setAttribute('aria-hidden', String(!current));
      slide.inert = !current;
    });

    this.dots.forEach((dot, dotIndex) => {
      const current = dotIndex === index;
      dot.classList.toggle('is-current', current);
      dot.setAttribute('aria-selected', String(current));
      dot.tabIndex = current ? 0 : -1;
    });

    this.centerActiveDot();
    if (announce && this.status) this.status.textContent = this.statusText(index);
  }

  centerActiveDot() {
    if (!this.dotList) return;
    const dot = this.dots[this.activeIndex];
    const left = dot.offsetLeft - (this.dotList.clientWidth - dot.offsetWidth) / 2;
    this.dotList.scrollTo({ left: Math.max(0, left), behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
  }

  statusText(index) {
    const template = this.dataset.statusTemplate || '__INDEX__ / __TOTAL__: __TITLE__';
    return template
      .replace('__INDEX__', String(index + 1))
      .replace('__TOTAL__', String(this.slides.length))
      .replace('__TITLE__', this.slides[index].dataset.slideTitle || '');
  }

  pauseByUser() {
    this.userPaused = true;
    this.syncAutoplay();
  }

  togglePlayback() {
    if (!this.autoplayEnabled || this.reducedMotion.matches) return;

    if (this.userPaused || !this.canAutoplay()) {
      this.userPaused = false;
      this.hovered = false;
      this.focusWithin = false;
    } else {
      this.userPaused = true;
    }
    this.syncAutoplay();
  }

  clearAutoplay() {
    clearTimeout(this.autoplayTimer);
    this.autoplayTimer = 0;
    this.classList.remove('is-playing');
  }

  canAutoplay() {
    return (
      this.autoplayEnabled &&
      !this.reducedMotion.matches &&
      !this.userPaused &&
      this.inView &&
      !document.hidden &&
      !this.hovered &&
      (!this.focusWithin || document.activeElement === this.playback) &&
      !this.dragging
    );
  }

  syncAutoplay() {
    this.clearAutoplay();
    const playing = this.canAutoplay();

    if (playing) {
      this.classList.add('is-playing');
      this.autoplayTimer = window.setTimeout(() => {
        /* Autoplay loops: past the last slide it springs back to the first.
           Pause/hover/focus/drag/out-of-view guards above keep this from
           running unattended in front of a reader who asked it to stop. */
        this.animateTo((this.activeIndex + 1) % this.slides.length, 0, false);
        this.syncAutoplay();
      }, this.autoplayDelay);
    }

    const state = playing ? 'pause' : 'play';
    this.setPlaybackState(state);
  }

  setPlaybackState(state) {
    if (!this.playback) return;
    this.playback.dataset.state = state;
    const labels = {
      play: this.dataset.playLabel || 'Play carousel',
      pause: this.dataset.pauseLabel || 'Pause carousel'
    };
    this.playback.setAttribute('aria-label', labels[state]);
  }

  syncPlaybackAvailability() {
    if (!this.playback) return;
    this.playback.hidden = !this.autoplayEnabled || this.reducedMotion.matches;
  }
}

if (!customElements.get('product-carousel')) customElements.define('product-carousel', ProductCarousel);
